import { ChangeDetectorRef, Component, DestroyRef, effect, EventEmitter, inject, OnInit, Output, output, signal } from '@angular/core';
import { ChatService } from './chat.service';
import { take } from 'rxjs';
import { UserService } from '../services/user.service';
import { FirebaseService } from '../services/firebase.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';
import { create } from 'domain';
import { WorkService } from '../work/work.service';
import { HttpResponse } from '@angular/common/http';
import { IndexedDBService } from '../services/indexed-db.service';
import { read } from 'fs';
import { Router } from '@angular/router';

export function getThumb(chat){
  const image = chat.picture;
  if(!image){
    if(['InventoryAction::BuyOrder','InventoryAction::Stock'].indexOf(chat.super_type) > -1){
      return 'https://firebasestorage.googleapis.com/v0/b/cardcontrol-4793f.appspot.com/o/supicon.png?alt=media&token=e0c9ac0b-3e39-4a15-9063-2b3a69532e8a'
    }
    if(['Booking'].indexOf(chat.super_type) > -1){
      return 'https://firebasestorage.googleapis.com/v0/b/cardcontrol-4793f.appspot.com/o/booking.png?alt=media&token=5a2b1c4d-8e6f-4a7b-9c2d-0e5f3a1b8c7b'
    }
    return null;
  }
  return image.replace(image, `${image}thumb@256_`);
}

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
  standalone: false
})
export class ChatComponent implements OnInit {
  private chatService = inject(ChatService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private users = [];
  private userService = inject(UserService);
  private firebase = inject(FirebaseService);
  private workService = inject(WorkService);
  private readonly destroyRef = inject(DestroyRef);
  private indexedDB = inject(IndexedDBService);
  showArchived = false;
  chats = [];
  selectedChat = signal(null);
  loading = signal(true);
  showIA = signal(false);
  loadingIA = signal(false);
  filteredChats = [];
  subscriptions = [];
  newComments = [];
  lastChatId = localStorage.getItem('lastChat');
  documentTypes: { name: string, icon: string }[] = [
    { name: 'Order', icon: 'description' },
    { name: 'Budget', icon: 'attach_money' },
    { name: 'InventoryAction', icon: 'inventory' }
  ];
  user = null;
  searchTerm: string = '';
  searching = signal(false);
  
  unreadMessages = output<any>();
  @Output() openChatEvent = new EventEmitter<any>();
  soundListener: any;
  lastSound = new Date();
  unreadArchived: number = 0;
  workCount: number = 0;
  chatHistory = [];
  
  constructor() {
    
    this.userService.getUsers().subscribe((users) => {
      this.users = users.filter((user) => user.role);
    });
    this.userService.user$.subscribe((user) => {
      this.user = user;
    });
    effect(() => {
      if(this.selectedChat() === null){
        localStorage.removeItem('lastChat');
        this.lastChatId = null;
        this.chatHistory.pop();
        if(this.chatHistory.length !== 0){
          const chat = this.chats.find((chat) => chat.id === this.chatHistory[this.chatHistory.length - 1]);
          if(chat){
            this.openChat(chat, false);
          } 
        }
      }
      if(this.selectedChat() && this.selectedChat().id){
        const chatId = this.selectedChat().id;
        if (!this.chatHistory.includes(chatId)) {
          this.chatHistory.push(chatId);
        }
      }
    });
  }
  

  filterChats() {
    this.filteredChats = this.chats.filter(chat =>
      chat.subtitle?.toLowerCase().includes(this.searchTerm.toLowerCase()) || 
      chat.title?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      chat.document_type?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      chat.correlative_id?.toString().toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      chat.status_name?.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
    if(this.searchTerm.length > 0){
      this.searching.set(true);
    }else{
      this.searching.set(false);
    }
  }
  filteredChatsByType(type: any) {
    if(this.searching()){
      return this.filteredChats;
    }
    else{
      return this.filteredChats.filter(chat => chat.document_type === type.name);
    }
  }
  getMentionCountByType(type: any): number {
    return this.chats
      .filter(chat => chat.document_type === type.name)
      .reduce((count, chat) => count + (chat.mention || 0), 0);
  }
  hasUnreadByType(type: any): boolean {
    return this.chats.some(chat => chat.document_type === type.name && chat.unread > 0 && chat.listed);
  }
  getChats() {
    this.indexedDB.read('messages').then(savedMessages => {
      savedMessages = savedMessages || [];
      
      this.chatService.index().subscribe(
        {
          next: (ownerships) => {
            const chats = ownerships.map((ownership) => {
              const chat = ownership;
              chat.comments = savedMessages.filter(message => {
                return message.commentable_type === chat.document_type && message.commentable_id === chat.document_id;
              });
              this.processNewMessages(chat);
              return chat;
            });
            this.chats = [...chats];
            this.filteredChats = [...chats];
            this.openLastChat();
            this.cdr.detectChanges();
          },
          error: (error) => {
            console.error('Error fetching chats:', error);
          },
          complete: () => {
            this.workService.own().pipe(take(1)).subscribe((response: any) => {
              this.workCount = response.body.length;
              this.chats.forEach((chat) => {
                const works = response.body.filter((work) => work.document_id === chat.document_id && work.document_type === chat.document_type);
                if (works.length > 0) {
                  chat.works = works;
                }
              });
              this.sortChats();
              this.subcribeToChats();
            });
          }
        }
      );
    })
    
  }
  ngOnInit() {
    this.getChats();

    this.chatService.openHistory$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      const chat = this.chats.find((chat) => chat.document_id === data.document_id && chat.document_type === data.document_type);
      if(chat){
        this.openChat(chat, data.switchToChatView ?? true);
      }else if(data.chat_id){
        this.indexedDB.read('chats', data.chat_id).then((chat) => {
          if(chat){
            this.openChat(chat, data.switchToChatView ?? true);
          }
        });
      }else{
        
      }
    });

    this.chatService.newComments$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((comment) => {
      const chat = this.chats.find((chat) => chat.document_id === comment.commentable_id && chat.document_type === comment.commentable_type);
      if (chat && (comment.target_id === this.user.id || comment.target_type !== 'User')) {
        this.processNewMessages(chat,comment)
        this.sortChats();
      }
    });

    this.chatService.reloadChats$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.getChats();
    });

    this.firebase.subscribeToMessages('added-to-document',null, (chat) => {
      this.chatService.writeAndOpenChat(null, chat);
      this.cdr.markForCheck();
      const findChat = this.chats.find((fchat) => fchat.document_id === chat.document_id && fchat.document_type === chat.document_type);
      if(findChat){
        
      }else{
        const newChat ={
          ...chat,
          listed: !chat.finished,
          unread: 1,
          mention: 1,
        }
        
        this.chats.push(newChat);
        this.processNewMessages(newChat);
      }
      this.chats = [...this.chats];
      this.filteredChats = [...this.chats];
      this.sortChats();
      this.subcribeToChats();
      this.cdr.detectChanges();
    }, this.destroyRef);

    this.firebase.subscribeToMessages(
      'document-readed',
      null,
      (data) => {
        const chat = this.chats.find((chat) => chat.document_id === data.document_id && chat.document_type === data.document_type);
        if(chat){
          chat.readed_at = data.readed_at;
          this.processNewMessages(chat);
          this.sortChats();
        }
      },
      this.destroyRef
    );

    this.firebase.subscribeToMessages('new-work',null, (data) => {
      this.getChats();
    }, this.destroyRef);
  }
  openChat(chat, switchToChatView = true) {
    if(chat){
      chat.unread = 0;
      chat.mention = 0;
      this.selectedChat.set({...chat});
      this.lastChatId = chat.id;
      localStorage.setItem('lastChat', chat.id);
      if (switchToChatView) {
        this.openChatEvent.emit(chat);
      }
      this.cdr.detectChanges();
    }
  }
  goBack() {
    this.selectedChat.set(null);
    this.showIA.set(false);
    this.getChats();
  }
  getThumb(chat){
    return getThumb(chat);
  }
  subcribeToChats(){
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.subscriptions = [];
    this.chats.forEach((chat) => {
      const path = `comments/${chat.document_type}-${chat.document_id}`;
      this.subscriptions.push(this.firebase.subscribeToNew(path, chat.readed_at, (comment) => {
        this.chatService.newComments$.next(comment);
      }, this.destroyRef));
    });
  }
  isToday(dateString: string): boolean {
    const date = new Date(dateString);
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }

  emitUnreadMessages(){
    let unreadCount = 0;
    let mentionCount = 0;
    this.unreadArchived = 0;
    this.chats.forEach((chat) => {
      if(chat.unread > 0 && chat.listed){
        unreadCount += chat.unread;
      }
      if(chat.mention > 0){
        mentionCount += chat.mention;
        if(!chat.listed){
          this.unreadArchived += chat.mention;
        }
      }
    });
    this.unreadMessages.emit({
      unread: unreadCount,
      mention: mentionCount,
      unread_archived: this.unreadArchived,
      workCount: this.workCount
    });
  }

  newMessageSound(comment){
    if(comment.user_id !== this.user.id){
      this.soundListener = setTimeout(() => {
        if(this.soundListener){
          clearTimeout(this.soundListener);
        }
        const audio = new Audio();
        audio.src = 'assets/sounds/alert.mp3';
        audio.load();
        audio.play();
      }, 1000);
    }
  }
  
  sortChats() {
    
    this.chats.sort((a, b) => {
      const aLastCommentDate = a.last_comment?.created_at || a.created_at;
      const bLastCommentDate = b.last_comment?.created_at || b.created_at;
      
      const nearestDate = bLastCommentDate.localeCompare(aLastCommentDate);
      const hasWork = b.works && b.works.length > 0;
      const hasWork2 = a.works && a.works.length > 0;
      const hasMention = b.mention > 0;
      const hasMention2 = a.mention > 0;
      const isFinished = !b.listed;
      const isFinished2 = !a.listed;
      if (hasWork && !hasWork2) {
        return 1;
      }
      if (!hasWork && hasWork2) {
        return -1;
      }
      if (hasMention && !hasMention2) {
        return 1;
      }
      if (!hasMention && hasMention2) {
        return -1;
      }
      if (isFinished && !isFinished2) {
        return -1;
      }
      if (!isFinished && isFinished2) {
        return 1;
      }
      if (nearestDate !== 0) {
        return nearestDate;
      }

    });
    this.filteredChats = this.chats
    this.loading.set(false);
    this.emitUnreadMessages();
  }
  openLastChat() {
    if(this.lastChatId){
      const chat = this.chats.find((chat) => chat.id === parseInt(this.lastChatId));
      if(chat){
        this.openChat(chat, true);
      }else{
        this.selectedChat.set(null);
      }
    }else{
      this.selectedChat.set(null);
    }
    this.cdr.markForCheck();
  }
  setChatLastComment(chat,comment?){
    if(comment){
      chat.last_comment = comment;
    }
    else{
      if(chat.comments && chat.comments.length > 0){
        chat.last_comment = chat.comments.sort((a, b) => {
          return b.created_at.localeCompare(a.created_at);
        })[0];
      }else{
        chat.last_comment = {
          created_at: chat.created_at,
          comment: 'Te has unido al chat'
        }
        chat.comments = [{
          created_at: chat.created_at,
          comment: 'Te has unido al chat'
        }];
      }
    }
  }
  processNewMessages(chat,comment?){
    this.cdr.markForCheck();
    let write
    if(comment){
      const c = chat.comments.find((c) => c.id === comment.id);
      if(!c){
        chat.comments.push(comment);
        write = this.indexedDB.write('messages', {
          ...comment,
        });
      }
    }
    this.setChatLastComment(chat,comment);
    
    
    if(write){
      write.onsuccess = () => {
        this.checkMention(chat);
        
      }
    }else{
      this.checkMention(chat);
      
    }
  }
  checkMention(chat){
    this.indexedDB.read('messages', [chat.document_type, chat.document_id], 'commentable').then((messages) => {
      const chatNewComments = messages.filter((c) => {
        return c.created_at.localeCompare(chat.readed_at || chat.created_at) > 0 && c.commentable_id === chat.document_id && c.commentable_type === chat.document_type && (c.author_id !== this.user.id || c.target_type !== 'User');
      })
      const ownMessagesIds = messages.filter((message) => this.user.id === message.author_id).map((message) => message.id);
      chat.mention = chatNewComments.filter((c) => {
        return c.target_id === this.user.id || c.comment.indexOf(`(user:${this.user.id})`) > -1 || (c.target_type == 'Comment' && ownMessagesIds.indexOf(c.target_id) > -1);
      }).length;
      chat.unread = chatNewComments.length;
      this.sortChats();
    })
  }
  openProfile(){
    this.router.navigate(['main','settings']);
  }
  toggleArchived(){
    this.showArchived = !this.showArchived;
  }

  toggleIA(){
    this.loadingIA.set(true);
    this.chatService.get('agent').subscribe((response) => {
      this.loadingIA.set(false);
      this.selectedChat.set(response);
      
    }, (error) => {
      this.loadingIA.set(false);
      console.error('Error fetching IA chat', error);
    }
    );
  }
}
